import { useCallback, useEffect, useState } from "react";
import { ArrowRight, MapPin, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { API_URL } from "../../config";

/**
 * Rich text from the Jodit editor can bake in fixed text colors (e.g. black or
 * white) that don't adapt to light/dark mode. We strip only GRAYSCALE colors so
 * that text falls back to the theme color, while keeping saturated accents (e.g.
 * orange) exactly as the author set them.
 */
function colorToRgb(color: string): [number, number, number] | null {
  if (typeof document === "undefined") return null;
  const ctx = document.createElement("canvas").getContext("2d");
  if (!ctx) return null;
  // Canvas normalizes any valid CSS color; invalid values leave the prior value.
  ctx.fillStyle = "#000000";
  ctx.fillStyle = color;
  const normalized = ctx.fillStyle;
  if (normalized.startsWith("#")) {
    return [
      parseInt(normalized.slice(1, 3), 16),
      parseInt(normalized.slice(3, 5), 16),
      parseInt(normalized.slice(5, 7), 16),
    ];
  }
  const match = normalized.match(/rgba?\(([^)]+)\)/);
  if (match) {
    const [r, g, b] = match[1].split(",").map((n) => parseFloat(n));
    return [r, g, b];
  }
  return null;
}

function isGrayscale(color: string): boolean {
  const rgb = colorToRgb(color);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  // Low channel spread = black / white / gray. Saturated colors (orange) have a wide spread.
  return Math.max(r, g, b) - Math.min(r, g, b) <= 18;
}

function neutralizeGrayscaleColors(html?: string): string {
  if (!html) return "";
  if (typeof window === "undefined" || !window.DOMParser) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll<HTMLElement>("[style]").forEach((el) => {
    const color = el.style.color;
    if (color && isGrayscale(color)) {
      el.style.removeProperty("color");
      if (!el.getAttribute("style")?.trim()) el.removeAttribute("style");
    }
  });
  return doc.body.innerHTML;
}

/**
 * Homepage hero — design asset: 1920×600px (16:5 aspect ratio).
 * Uses aspect-ratio (not fixed heights) so the whole banner scales down uniformly
 * as the screen narrows — like Flipkart — instead of cropping via background-cover.
 * min-height keeps the overlay text readable on very small phones.
 */
const HERO_FRAME_CLASS =
  "relative w-full max-w-[1536px] mx-auto aspect-[16/5] min-h-[300px]";

/** Shimmer placeholder shown while banners load (e.g. on hard refresh). */
export function HeroSkeleton() {
  return (
    <section className="relative overflow-hidden w-full bg-background">
      <div className={`${HERO_FRAME_CLASS} bg-surface animate-pulse`}>
        <div className="absolute inset-0 bg-gradient-to-r from-surface-elevated via-surface to-surface" />
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
          <div className="max-w-2xl space-y-4">
            <div className="h-4 w-32 rounded bg-surface-elevated" />
            <div className="h-10 sm:h-14 w-3/4 rounded-lg bg-surface-elevated" />
            <div className="h-10 sm:h-14 w-1/2 rounded-lg bg-surface-elevated" />
            <div className="h-4 w-2/3 rounded bg-surface-elevated" />
            <div className="mt-6 flex gap-3">
              <div className="h-12 w-40 rounded-xl bg-surface-elevated" />
              <div className="h-12 w-36 rounded-xl bg-surface-elevated" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Hero({ banners }: { banners: any[] }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return;
    const intervalId = setInterval(() => {
      emblaApi.scrollNext();
    }, 6000);
    return () => clearInterval(intervalId);
  }, [emblaApi]);

  if (!banners || banners.length === 0) return null;

  return (
    <section className="relative overflow-hidden group w-full bg-background">
      <div className="overflow-hidden max-w-[1536px] mx-auto" ref={emblaRef}>
        <div className="flex">
          {banners.map((banner, index) => {
            const desktopSrc = banner.image ? `${API_URL}/${banner.image.replace(/\\/g, '/')}` : '';
            const hasMobile = !!banner.mobileImage;
            const mobileSrc = hasMobile ? `${API_URL}/${banner.mobileImage.replace(/\\/g, '/')}` : desktopSrc;
            // Mobile uses a taller 6:5 frame (recommended upload 1200x1000) so the overlaid
            // heading/text/buttons have room; desktop keeps the wide 16:5 frame.
            const frameClass = hasMobile
              ? "relative w-full aspect-[6/5] sm:aspect-[16/5]"
              : "relative w-full aspect-[16/5] min-h-[300px]";

            return (
            <div key={banner._id || index} className="flex-[0_0_100%] min-w-0 relative w-full">
              {/* Banner frame */}
              <div className={frameClass}>
              {/* Background image - mobile variant (phones) */}
              <div
                aria-hidden
                className="absolute inset-0 bg-cover bg-center sm:hidden theme-banner-media"
                style={{ 
                    backgroundImage: `url(${mobileSrc})` 
                }}
              />
              {/* Background image - desktop variant */}
              <div
                aria-hidden
                className="absolute inset-0 bg-cover bg-center hidden sm:block theme-banner-media"
                style={{ 
                    backgroundImage: `url(${desktopSrc})` 
                }}
              />
              {/* Soft wash only under the copy column — right side of the photo stays bright */}
              <div
                aria-hidden
                className={`absolute inset-y-0 left-0 w-[70%] sm:w-[55%] lg:w-[48%] bg-gradient-to-r transition-opacity duration-700 pointer-events-none ${
                  banner.overlayColor === 'orange'
                    ? 'from-primary/55 via-primary/20 to-transparent'
                    : 'from-black/40 via-black/15 to-transparent'
                }`}
              />

              <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center py-7 lg:py-9">
                <div
                  className={`max-w-2xl transition-all duration-1000 ${
                    selectedIndex === index
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-10'
                  }`}
                >
                  {banner.tag && (
                    <div
                      className={`mb-3 text-[10px] lg:text-xs font-bold tracking-[0.3em] uppercase [text-shadow:0_1px_8px_rgba(0,0,0,0.65)] ${
                        banner.overlayColor === 'orange' ? 'text-white/95' : 'text-primary'
                      }`}
                    >
                      {banner.tag}
                    </div>
                  )}

                  {banner.badgeText && (
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-medium mb-2 ${
                        banner.overlayColor === 'orange'
                          ? 'text-white bg-white/20'
                          : 'text-primary bg-black/50 border border-primary/30'
                      }`}
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      {banner.badgeText}
                    </div>
                  )}

                  <h1 className="mt-2 text-[1.65rem] sm:text-[2.7rem] lg:text-[clamp(1.75rem,4vw,3.35rem)] xl:text-[3.9rem] font-display font-bold leading-[1.1] sm:leading-[1.05] tracking-tight text-white [text-shadow:0_2px_16px_rgba(0,0,0,0.7),0_1px_3px_rgba(0,0,0,0.85)]">
                    <span dangerouslySetInnerHTML={{ __html: banner.title || '' }} />
                    <br />
                    <span
                      className={
                        banner.overlayColor === 'orange'
                          ? 'text-white/95'
                          : 'text-gradient-brand'
                      }
                      style={
                        banner.overlayColor === 'orange'
                          ? undefined
                          : { filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.65))' }
                      }
                      dangerouslySetInnerHTML={{ __html: banner.subtitle || '' }}
                    />
                  </h1>

                  <p
                    className="mt-4 max-w-lg text-sm sm:text-base lg:text-lg leading-relaxed text-white [text-shadow:0_1px_10px_rgba(0,0,0,0.7),0_1px_2px_rgba(0,0,0,0.8)]"
                    dangerouslySetInnerHTML={{
                      __html: neutralizeGrayscaleColors(banner.description),
                    }}
                  />

                  <div className="mt-6 flex flex-wrap gap-3">
                    {banner.buttonText_1 && (
                      <a
                        href={banner.buttonLink_1 || '/products'}
                        className={`group inline-flex items-center justify-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 rounded-xl font-semibold transition-all hover:-translate-y-0.5 text-sm sm:text-base ${
                          banner.overlayColor === 'orange'
                            ? 'bg-white text-primary shadow-lg shadow-white/20 hover:bg-white/90'
                            : 'bg-gradient-brand-strong text-primary-foreground shadow-glow-purple hover:shadow-glow-cyan'
                        }`}
                      >
                        {banner.buttonText_1}
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </a>
                    )}
                    {banner.buttonText_2 && (
                      <a
                        href={banner.buttonLink_2 || '/about'}
                        className={`inline-flex items-center justify-center gap-2 px-5 py-3 sm:px-6 sm:py-3.5 rounded-xl font-semibold transition-all border text-sm sm:text-base ${
                          banner.overlayColor === 'orange'
                            ? 'border-white/40 bg-white/10 backdrop-blur-md text-white hover:bg-white/20 hover:border-white'
                            : 'border-white/50 bg-black/40 text-white hover:bg-black/55 hover:border-primary/50'
                        }`}
                      >
                        <MapPin className="h-4 w-4" /> {banner.buttonText_2}
                      </a>
                    )}
                  </div>
                </div>
              </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Navigation arrows */}
      {banners.length > 1 && (
        <>
          <button
            onClick={() => emblaApi?.scrollPrev()}
            className="absolute left-4 xl:left-[max(1rem,calc(50%-768px))] top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass flex items-center justify-center text-foreground/70 hover:text-primary transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={() => emblaApi?.scrollNext()}
            className="absolute right-4 xl:right-[max(1rem,calc(50%-944px))] top-1/2 -translate-y-1/2 w-10 h-10 rounded-full glass flex items-center justify-center text-foreground/70 hover:text-primary transition-all opacity-0 group-hover:opacity-100"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi?.scrollTo(i)}
                className={`w-2 h-2 rounded-full transition-all ${selectedIndex === i ? 'bg-primary w-6' : 'bg-white/30'}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
